"use client"

import { useState } from "react"

import type { IdentitySetup } from "@/lib/identity-setup"

type SetupLayoutProps = {
  setup: IdentitySetup
  onRetry: () => void
  onLogout: () => void | Promise<void>
  onCompleteSetup: () => Promise<void>
}

/**
 * Full-page placeholder until workspace/user finalize APIs exist.
 */
export function SetupLayout({ setup, onRetry, onLogout, onCompleteSetup }: SetupLayoutProps) {
  const [completing, setCompleting] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)

  const handleCompleteSetup = async () => {
    setCompleting(true)
    setCompleteError(null)
    try {
      await onCompleteSetup()
    } catch (e) {
      setCompleteError(e instanceof Error ? e.message : String(e))
    } finally {
      setCompleting(false)
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white px-8 py-4 shadow-sm">
        <h1 className="text-lg font-semibold tracking-tight">Configuration required</h1>
        <p className="mt-1 text-sm text-slate-600">
          Your account needs a short setup step before you can use the app.
        </p>
      </header>
      <div className="flex flex-1 flex-col items-center justify-center gap-8 px-8 py-12">
        <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <p className="text-sm text-slate-600">
            Placeholder: workspace and user onboarding UI will go here (forms, review, submit).
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-center gap-2">
              <span
                className={
                  setup.workspace ? "font-medium text-amber-700" : "font-medium text-emerald-700"
                }
              >
                {setup.workspace ? "Pending" : "Done"}
              </span>
              <span className="text-slate-700">Workspace setup</span>
            </li>
            <li className="flex items-center gap-2">
              <span
                className={setup.user ? "font-medium text-amber-700" : "font-medium text-emerald-700"}
              >
                {setup.user ? "Pending" : "Done"}
              </span>
              <span className="text-slate-700">User setup</span>
            </li>
          </ul>
          {completeError ? (
            <p className="mt-4 text-sm text-red-700" role="alert">
              {completeError}
            </p>
          ) : null}
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              disabled={completing}
              onClick={() => void handleCompleteSetup()}
            >
              {completing ? "Completing…" : "Complete Setup"}
            </button>
            <button
              type="button"
              className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
              onClick={() => void onRetry()}
            >
              Retry check
            </button>
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              onClick={() => void onLogout()}
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </main>
  )
}
